import React from 'react';
import ReactDOM from 'react-dom';

import Data, { _F } from 'data';
import Store from 'store';
import { ItemIcon, WithEngravingTooltip, WithIslandTooltip, WithItemTooltip, WithMoneyTooltip, WithMusicTooltip, WithSkillTooltip, WithSocialActionTooltip, WithTripodTooltip } from 'tooltip';
import { getIconPath } from 'common';

import 'components/EngravingView.scss';
import classNames from 'classnames';

function WrapperNode({ html, children, ...props }) {
  if (html) return <span {...props} dangerouslySetInnerHTML={{__html: html}}/>;
  return <span {...props}>{children}</span>;
}

export function MoneyNode({ id, text, html, icon }) {
  const data = Data.money[id];
  if (!data || (icon && !data.icon)) return <WrapperNode html={html}>{text}</WrapperNode>;
  return (
    <WithMoneyTooltip id={id}>
      <WrapperNode className={classNames("la-gametip", {"lap-icon-only": icon})} html={html}>
        {!!data.icon && <span className="lap-money-icon" style={{backgroundImage: `url(${getIconPath(data.icon)})`}}/>}
        {!icon && <span className="lap-money-name">{text}</span>}
      </WrapperNode>
    </WithMoneyTooltip>
  );
}

export function ItemNode({ id, text, html, icon }) {
  const money = Data.money[id];
  if (money) return <MoneyNode id={id} text={text} html={html} icon={icon}/>
  const item = Data.items[id];
  if (!item || (icon && !item.icon)) return <WrapperNode html={html}>{text}</WrapperNode>;
  const color = Data.itemGrades[item.grade || 0].color;
  return (
    <WithItemTooltip item={id}>
      <WrapperNode className={classNames("la-gametip", {"lap-icon-only": icon})} html={html}>
        {!!item.icon && (
          <div className={`lap-item-background lap-item-${color}`}>
            <ItemIcon item={item}/>
          </div>
        )}
        {!icon && (
          <span className={`la-color-${color}`}>
            {text}
          </span>
        )}
      </WrapperNode>
    </WithItemTooltip>
  );
}

function SocialActionNode({ id, text, html, icon }) {
  const data = Data.socialAction[id];
  if (!data || (icon && !data.icon)) return <WrapperNode html={html}>{text}</WrapperNode>;
  return (
    <WithSocialActionTooltip id={id}>
      <WrapperNode className={classNames("la-gametip", {"lap-icon-only": icon})} html={html}>
        {!!data.icon && <span className="lap-skill-icon" style={{backgroundImage: `url(${getIconPath(data.icon)})`}}/>}
        {!icon && <span className="lap-social-name">{text}</span>}
      </WrapperNode>
    </WithSocialActionTooltip>
  );
}

function MusicNode({ id, text, html, icon }) {
  const data = Data.music[id];
  if (!data || (icon && !data.icon)) return <WrapperNode html={html}>{text}</WrapperNode>;
  return (
    <WithMusicTooltip id={id}>
      <WrapperNode className={classNames("la-gametip", {"lap-icon-only": icon})} html={html}>
        {!!data.icon && <span className="lap-skill-icon" style={{backgroundImage: `url(${getIconPath(data.icon)})`}}/>}
        {!icon && <span className="lap-music-name">{text}</span>}
      </WrapperNode>
    </WithMusicTooltip>
  );
}

function IslandNode({ id, text, html, icon }) {
  const data = Data.zone[id];
  if (!data || !data.island || (icon && !data.island.icon)) return <WrapperNode html={html}>{text}</WrapperNode>;
  return (
    <WithIslandTooltip id={id}>
      <WrapperNode className={classNames("la-gametip", {"lap-icon-only": icon})} html={html}>
        {!!data.island.icon && <span className="lap-skill-icon" style={{backgroundImage: `url(${getIconPath(data.island.icon)})`}}/>}
        {!icon && <span className="lap-island-name">{text}</span>}
      </WrapperNode>
    </WithIslandTooltip>
  );
}

function parseTripods(str) {
  const tripods = {};
  for (const m of str.matchAll(/([a-z]?)(\d)/g)) {
    if (m[1]) {
      tripods[m[1].charCodeAt(0) - 96] = parseInt(m[2]);
    } else {
      tripods[m[2]] = 1;
    }
  }
  return tripods;
}

function SkillNode({ id, level, tripods, rune, text, html, pvp }) {
  const skill = Data.skills[id];
  if (!skill) return <WrapperNode html={html}>{text}</WrapperNode>;
  const data = {id};
  if (level) data.level = parseInt(level);
  if (tripods) data.tripods = parseTripods(tripods);
  if (rune) data.rune = parseInt(rune);
  return (
    <WithSkillTooltip skill={data} pvp={!!pvp}>
      <WrapperNode className="la-gametip" html={html}>
        {!!skill.icon && <span className="lap-skill-icon" style={{backgroundImage: `url(${getIconPath(skill.icon)})`}}/>}
        <span className={data.awakening ? "lap-awakening-name" : "lap-skill-name"}>
          {text}
        </span>
      </WrapperNode>
    </WithSkillTooltip>
  );
}

function TripodNode({ skill, index, text, html, level, pvp }) {
  const data = Data.skills[skill]?.tripods[parseInt(index) - 1];
  if (!data) return <WrapperNode html={html}>{text}</WrapperNode>;
  return (
    <WithTripodTooltip skill={skill} tripod={parseInt(index)} level={level} pvp={!!pvp}>
      <WrapperNode className="la-gametip" html={html}>
        {!!data.icon && <span className="lap-tripod-icon" style={{backgroundImage: `url(${getIconPath(data.icon)})`}}/>}
        <span className="lap-tripod-name">
          {text}
        </span>
      </WrapperNode>
    </WithTripodTooltip>
  );
}

function EngravingIconNode({ id, level, text, html }) {
  const engraving = Data.engraving[id];
  if (!engraving || !engraving.icon) return <WrapperNode html={html}>{text}</WrapperNode>;
  level = (level && parseInt(level)) || 3;
  return (
    <WithEngravingTooltip id={id} level={level}>
      <span className="la-gametip">
        <div className="lap-EngravingIcon" style={{backgroundImage: `url(${getIconPath(engraving.icon)})`}}>
          <div className={`lap-engraving-frame lap-level-${level}`}><span>{_F('sys.tooltip.engrave_active_level', level)}</span></div>
        </div>
      </span>
    </WithEngravingTooltip>
  );
}

function EngravingNode({ id, level, text, html }) {
  const engraving = Data.engraving[id];
  if (!engraving) return <WrapperNode html={html}>{text}</WrapperNode>;
  return (
    <WithEngravingTooltip id={id} level={level && parseInt(level)}>
      <WrapperNode className="la-gametip" html={html}>
        {!!engraving.icon && <span className="lap-tripod-icon" style={{backgroundImage: `url(${getIconPath(engraving.icon)})`}}/>}
        <span className="lap-engraving-name">
          {text}
        </span>
      </WrapperNode>
    </WithEngravingTooltip>
  );
}

function nodeContent(node) {
  if (node.childNodes.length === 1 && node.childNodes[0].nodeType === 3) {
    return {text: node.textContent};
  } else {
    return {html: node.innerHTML};
  }
}

export default async function makeGameTips() {
  const store = new Store({ embed: true });
  function render(elem, node) {
    if (elem) {
      ReactDOM.render((
        <Store.Provider store={store}>
          {elem}
        </Store.Provider>
      ), node);
    }
  }

  function createNode(node) {
    const content = nodeContent(node);
    if (node.classList.contains("la-item")) {
      const iid = node.getAttribute("data-la-id");
      Data.loadItem(iid).then(item => render(<ItemNode {...content} id={iid} icon={node.getAttribute("data-la-icon")}/>, node), () => null);
    } else if (node.classList.contains("la-item-icon")) {
      const iid = node.getAttribute("data-la-id");
      Data.loadItem(iid).then(item => render(<ItemNode {...content} id={iid} icon/>, node), () => null);
    } else if (node.classList.contains("la-social")) {
      const iid = node.getAttribute("data-la-id");
      render(<SocialActionNode {...content} id={iid} icon={node.getAttribute("data-la-icon")}/>, node);
    } else if (node.classList.contains("la-music")) {
      const iid = node.getAttribute("data-la-id");
      render(<MusicNode {...content} id={iid} icon={node.getAttribute("data-la-icon")}/>, node);
    } else if (node.classList.contains("la-island")) {
      const iid = node.getAttribute("data-la-id");
      render(<IslandNode {...content} id={iid} icon={node.getAttribute("data-la-icon")}/>, node);
    } else if (node.classList.contains("la-money")) {
      const iid = node.getAttribute("data-la-id");
      render(<MoneyNode {...content} id={iid} icon={node.getAttribute("data-la-icon")}/>, node);
    } else if (node.classList.contains("la-engraving")) {
      const iid = node.getAttribute("data-la-id");
      const level = node.getAttribute("data-la-level");
      render(<EngravingNode {...content} id={iid} level={level}/>, node);
    } else if (node.classList.contains("la-engraving-icon")) {
      const iid = node.getAttribute("data-la-id");
      const level = node.getAttribute("data-la-level");
      render(<EngravingIconNode {...content} id={iid} level={level}/>, node);
    } else if (node.classList.contains("la-skill")) {
      const iid = node.getAttribute("data-la-id");
      const level = node.getAttribute("data-la-level");
      const tripods = node.getAttribute("data-la-tripods");
      const rune = node.getAttribute("data-la-rune");
      const pvp = node.getAttribute("data-la-pvp");
      render(<SkillNode {...content} id={iid} level={level} tripods={tripods} rune={rune} pvp={!!pvp}/>, node);
    } else if (node.classList.contains("la-tripod")) {
      const skill = node.getAttribute("data-la-skill");
      const index = node.getAttribute("data-la-index");
      const pvp = node.getAttribute("data-la-pvp");
      const level = node.getAttribute("data-la-level");
      render(<TripodNode {...content} skill={skill} index={index} pvp={!!pvp} level={level}/>, node);
    }
  }

  for (const node of document.querySelectorAll(".la-item, .la-item-icon, .la-skill, .la-tripod, .la-engraving, .la-engraving-icon, .la-social, .la-music, .la-money, .la-island")) {
    createNode(node);
  }
}
